<template>
  <div class="company-users-dashboard">
    <!-- Main card with enhanced styling -->
    <div class="card dashboard-card">
      <div class="card-header">
        <div class="header-content">
          <strong class="title">{{ $t('dashboardCompany.irktIstifadilri') }}</strong>
          <div class="header-icon">
            <i class="fa fa-user-friends"></i>
          </div>
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="card-body loading-state">
        <div class="spinner-container">
          <div class="spinner"></div>
        </div>
        <p>{{ $t('login.yklnir') }}</p>
      </div>

      <!-- Empty state -->
      <div v-else-if="!companyUsers || companyUsers.length === 0" class="card-body empty-state">
        <div class="empty-icon">
          <i class="fa fa-users-slash"></i>
        </div>
        <p>{{ $t('common.noData', 'No users available') }}</p>
      </div>

      <!-- Users list -->
      <div v-else class="card-body">
        <transition-group name="list" tag="div" class="users-container">
          <div
            v-for="(user, i) in companyUsers"
            :key="`user-${i}`"
            class="user-item"
          >
            <div class="user-avatar">
              <img
                class="avatar-image"
                :src="getPhoto(user.user.photo, 'thumbnailCropped')"
                :alt="user.user.name"
                @error="handleImageError"
              >
            </div>

            <div class="user-content">
              <div class="user-name">
                {{ user.user.name }} {{ user.user.surname }}
              </div>
              <div class="user-details">
                <span class="user-position">{{ user.position }}</span>
                <ProfileTypeBadge
                  :profile="user.profileType"
                  class="profile-badge"
                />
              </div>
            </div>
          </div>
        </transition-group>
      </div>

      <div class="card-footer">
        <router-link
          :to="{name: 'companyProfile'}"
          class="view-more-btn"
        >
          <span>{{ $t('dashboardCompany.dahaOxGstr') }}</span>
          <i class="fa fa-arrow-right"></i>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from "vuex";
import {getPhoto, getProfileName} from "@/repositories/Utils";
import {getCompanyUsers} from "@/repositories/GeneralDataRepository";
import ProfileTypeBadge from "@/components/ProfileTypeBadge.vue";

export default {
  name: 'DashboardCompanyUsers',
  components: {ProfileTypeBadge},
  data() {
    return {
      companyUsers: [],
      loading: true,
      defaultAvatar: require('@/assets/images/dashboard/user.png')
    };
  },

  computed: {
    ...mapState({
      token: state => state.auth.token,
    })
  },

  mounted() {
    this.fetchCompanyUsers();
  },

  methods: {
    /**
     * Fetch company users data from API
     */
    fetchCompanyUsers() {
      this.loading = true;

      getCompanyUsers(this.token)
        .then(data => {
          if (data.code === 200) {
            this.companyUsers = data.users.slice(0, Math.min(data.users.length, 5));
          } else {
            this.companyUsers = [];
            console.error('Failed to fetch company users data:', data);
          }
        })
        .catch(error => {
          console.error('Error fetching company users data:', error);
          this.companyUsers = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * Get profile name from keyword
     * @param {string} keyword - Profile keyword
     * @return {string} - Localized profile name
     */
    getProfileName(keyword) {
      return getProfileName(keyword, this.$i18n);
    },

    /**
     * Get photo URL from path and size
     * @param {string} path - Path to the photo
     * @param {string} size - Size of the photo
     * @return {string} - URL of the photo
     */
    getPhoto(path, size) {
      if (!path) return this.defaultAvatar;
      return getPhoto(path, size);
    },

    /**
     * Handle image loading error
     * @param {Event} e - Error event
     */
    handleImageError(e) {
      e.target.src = this.defaultAvatar;
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/theme/_variables.scss";

.company-users-dashboard {
  // Card styling
  .dashboard-card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    overflow: hidden;
    margin-bottom: 1.5rem;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
    }
  }

  // Card header styling
  .card-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    padding: 1rem 1.25rem;

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .title {
        color: #2062FB;
        font-size: 1.1rem;
        font-weight: 600;
        margin: 0;
        letter-spacing: 0.5px;
      }

      .header-icon {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(32, 98, 251, 0.1);
        border-radius: 50%;
        color: #2062FB;
        font-size: 0.9rem;
      }
    }
  }

  // Card body styling
  .card-body {
    padding: 1.25rem;

    // Loading state
    &.loading-state {
      text-align: center;
      padding: 3rem 1.5rem;
      color: $theme-font-color;

      .spinner-container {
        margin-bottom: 1rem;

        .spinner {
          width: 40px;
          height: 40px;
          margin: 0 auto;
          border: 3px solid rgba(32, 98, 251, 0.2);
          border-top-color: #2062FB;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
      }

      p {
        margin: 0;
        font-size: 16px;
        color: lighten($theme-font-color, 15%);
      }
    }

    // Empty state
    &.empty-state {
      text-align: center;
      padding: 3rem 1.5rem;
      color: $theme-font-color;

      .empty-icon {
        margin-bottom: 1rem;
        font-size: 2.5rem;
        color: lighten($theme-font-color, 40%);
      }

      p {
        margin: 0;
        font-size: 16px;
        color: lighten($theme-font-color, 15%);
      }
    }
  }

  // Users container
  .users-container {
    position: relative;
  }

  // User item styling
  .user-item {
    display: flex;
    align-items: center;
    padding: 12px;
    margin-bottom: 12px;
    border-radius: 8px;
    transition: all 0.25s ease;
    position: relative;
    background-color: rgba(#f8f9fa, 0.5);
    border-left: 3px solid transparent;

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      background-color: rgba(#f8f9fa, 1);
      transform: translateX(4px);
      border-left-color: #2062FB;
    }

    // User avatar
    .user-avatar {
      flex-shrink: 0;
      margin-right: 15px;
      position: relative;

      .avatar-image {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid white;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;

        &:hover {
          transform: scale(1.05);
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
        }
      }
    }

    // User content
    .user-content {
      flex-grow: 1;
      overflow: hidden;

      .user-name {
        font-weight: 600;
        color: darken($theme-font-color, 10%);
        margin-bottom: 5px;
        font-size: 0.95rem;
      }

      .user-details {
        display: flex;
        align-items: center;
        flex-wrap: wrap;

        .user-position {
          font-style: italic;
          color: lighten($theme-font-color, 15%);
          font-size: 0.85rem;
          margin-right: 8px;
        }

        .profile-badge {
          margin-top: 2px;
        }
      }
    }
  }

  // Card footer styling
  .card-footer {
    background-color: #f8f9fa;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    padding: 1rem 1.25rem;
    text-align: center;

    .view-more-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.5rem 1.25rem;
      background-color: rgba(#2062FB, 0.08);
      color: #2062FB;
      border-radius: 50px;
      font-weight: 500;
      font-size: 0.9rem;
      transition: all 0.3s ease;
      text-decoration: none;

      i {
        margin-left: 8px;
        transition: transform 0.3s ease;
      }

      &:hover {
        background-color: #2062FB;
        color: white;
        box-shadow: 0 4px 8px rgba(32, 98, 251, 0.3);

        i {
          transform: translateX(4px);
        }
      }

      &:active {
        transform: scale(0.98);
      }
    }
  }

  // List transitions
  .list-enter-active, .list-leave-active {
    transition: all 0.5s;
  }
  .list-enter, .list-leave-to {
    opacity: 0;
    transform: translateY(30px);
  }

  // Animations
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @keyframes blink {
    50% { opacity: 0.2; }
  }

  // Responsive design
  @media (max-width: 992px) {
    .dashboard-card {
      margin-bottom: 1rem;
    }
  }

  @media (max-width: 768px) {
    .card-header {
      padding: 0.75rem 1rem;

      .header-content .title {
        font-size: 1rem;
      }
    }

    .card-body {
      padding: 1rem;
    }

    .user-item {
      padding: 10px;
      margin-bottom: 10px;

      .user-avatar {
        margin-right: 12px;

        .avatar-image {
          width: 45px;
          height: 45px;
        }
      }

      .user-content {
        .user-name {
          font-size: 0.9rem;
        }

        .user-details {
          flex-direction: column;
          align-items: flex-start;

          .user-position {
            margin-bottom: 3px;
            margin-right: 0;
          }
        }
      }
    }

    .card-footer {
      padding: 0.75rem 1rem;
    }
  }

  @media (max-width: 576px) {
    .user-item {
      padding: 8px;
      margin-bottom: 8px;

      .user-avatar {
        margin-right: 10px;

        .avatar-image {
          width: 40px;
          height: 40px;
        }
      }

      .user-content {
        .user-name {
          font-size: 0.85rem;
        }

        .user-details {
          .user-position {
            font-size: 0.8rem;
          }
        }
      }
    }

    .card-footer .view-more-btn {
      padding: 0.4rem 1rem;
      font-size: 0.85rem;
    }
  }
}
</style>
